import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useLocation } from "@reach/router"
import { useStaticQuery, graphql } from "gatsby"

import StructuredData, { organizationId, websiteId } from "./structured-data.js"
import articles from "../data/articles.js"

const SECTION_NAMES = {
    'blog': 'Blog',
    'case-studies': 'Case Studies',
    'careers': 'Careers',
}

const SEO = ({ title, description, image, article, headline, datePublished, dateModified, noindex }) => {
    const { pathname } = useLocation()
    const { site, allFile } = useStaticQuery(query)

    const {
        defaultTitle,
        titleTemplate,
        defaultDescription,
        siteUrl,
        defaultImage,
        twitterUsername,
      } = site.siteMetadata

      // Pages pass images as gatsbyImageData, an imported URL, or a "../assets/..." path; all need an absolute URL.
      const resolveImage = (img) => {
          const src = img?.images?.fallback?.src
              ?? (typeof img === 'string' && img.startsWith('../assets/')
                  ? allFile.nodes.find(node => node.relativePath === img.replace('../assets/', ''))?.publicURL
                  : img)
          if (!src || typeof src !== 'string') return `${siteUrl}${defaultImage}`
          return src.startsWith('http') ? src : `${siteUrl}${src}`
      }

      const path = pathname.endsWith('/') ? pathname : `${pathname}/`

      const seo = {
        title: title || defaultTitle,
        description: description || defaultDescription,
        image: resolveImage(image),
        url: `${siteUrl}${path}`,
      }

    // Page-level schema only comes from a page's own <SEO>, not the bare one rendered by Layout.
    const schema = []
    const segments = path.split('/').filter(Boolean)

    if (title && article) {
        const entry = articles.find(item => item.link === path)
        const published = datePublished ?? entry?.published ?? entry?.date
        const modified = dateModified ?? entry?.date ?? published

        schema.push({
            "@type": segments[0] === 'blog' ? "BlogPosting" : "Article",
            "@id": `${seo.url}#article`,
            headline: (headline ?? title.replace(/\s[–|-]\s54 Bit Studios$/, '')).slice(0, 110),
            description: seo.description,
            image: [seo.image],
            ...(published && { datePublished: published }),
            ...(modified && { dateModified: modified }),
            author: { "@type": "Organization", "@id": organizationId(siteUrl), name: "54 Bit Studios", url: `${siteUrl}/` },
            publisher: { "@id": organizationId(siteUrl) },
            isPartOf: { "@id": websiteId(siteUrl) },
            mainEntityOfPage: seo.url,
            inLanguage: "en-GB",
        })
    }

    if (title && segments.length && SECTION_NAMES[segments[0]]) {
        const crumbs = [
            { name: 'Home', url: `${siteUrl}/` },
            { name: SECTION_NAMES[segments[0]], url: `${siteUrl}/${segments[0]}/` },
        ]
        if (segments.length > 1) {
            crumbs.push({ name: headline ?? title.replace(/\s[–|-]\s54 Bit Studios$/, ''), url: seo.url })
        }
        schema.push({
            "@type": "BreadcrumbList",
            itemListElement: crumbs.map((crumb, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: crumb.name,
                item: crumb.url,
            })),
        })
    }

    return (
        <>
            <Helmet title={seo.title} titleTemplate={titleTemplate} htmlAttributes={{ lang: 'en-GB' }}>
                <meta name="description" content={seo.description} />
                {noindex && <meta name="robots" content="noindex, follow" />}
                <meta name="image" content={seo.image} />
                {/* Always the trailing-slash URL, so slashless duplicates consolidate onto one address. */}
                {seo.url && <link rel="canonical" href={seo.url} />}
                {seo.url && <meta property="og:url" content={seo.url} />}
                {(article ? true : null) && <meta property="og:type" content="article" />}
                {seo.title && <meta property="og:title" content={seo.title} />}
                {seo.description && (
                    <meta property="og:description" content={seo.description} />
                )}
                {seo.image && <meta property="og:image" content={seo.image} />}
                <meta name="twitter:card" content="summary_large_image" />
                {twitterUsername && (
                    <meta name="twitter:creator" content={twitterUsername} />
                )}
                {seo.title && <meta name="twitter:title" content={seo.title} />}
                {seo.description && (
                    <meta name="twitter:description" content={seo.description} />
                )}
                {seo.image && <meta name="twitter:image" content={seo.image} />}
            </Helmet>
            {schema.length > 0 && (
                <StructuredData data={{ "@context": "https://schema.org", "@graph": schema }} />
            )}
        </>
    )
}

export default SEO

SEO.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    article: PropTypes.bool,
    headline: PropTypes.string,
    datePublished: PropTypes.string,
    dateModified: PropTypes.string,
    noindex: PropTypes.bool,
}

SEO.defaultProps = {
    title: null,
    description: null,
    image: null,
    article: false,
    noindex: false,
}

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        siteUrl: url
        defaultImage: image
        twitterUsername
      }
    }
    allFile(filter: { sourceInstanceName: { eq: "assets" }, extension: { in: ["jpg", "jpeg", "png", "webp"] } }) {
      nodes {
        relativePath
        publicURL
      }
    }
  }
`
